/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Icon from 'src/components/ui/Icon/Icon';
import { useOverlay } from '../providers/OverlayProvider';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import Button, { ButtonToneProps } from '../components/ui/Button/Button';
import Divider from '../components/ui/Divider/Divider';
import Input, { ControlledInput } from '../components/ui/Input/Input';
import ListItemsMenu from '../components/layouts/ListItemsMenu/ListItemsMenu';
import {
  useAllCountriesForSelectionQuery,
  useCountryGroupCreate,
  useCountryGroupUpdateData,
  useOneCountryGroupQuery,
} from '../services/countries';
import { useCarriersQuery } from '../services/carrier';
import Image from '../components/ui/Image/Image';
import { useNavigationItems } from '../hooks/useNavigationItems';
import { useEventListeners, useOutsideClick } from '../hooks/utils';

type Props = {
  editMode?: boolean;
  onCustomClose?: () => void;
};

export function CountryGroupOverlay({ editMode, onCustomClose }: Props) {
  const { id } = useParams();
  const mainRef = React.useRef<HTMLDivElement>(null);
  const numericId = id ? Number(id) : 0;
  const { onClose } = useOverlay();
  const { t } = useTranslation();
  const createCountryGroupSchema = z.object({
    title: editMode ? z.string().optional() : z.string().min(1, t('general.title_required')),
    description: z.string().optional(),
    countries: z.array(z.object({ id: z.number() })).min(1, t('general.country_select_required')),
    carriers: z.array(z.object({ id: z.number() })).min(1, t('general.carrier_select_required')),
  });

  type CreateCountryGroupFormValues = z.infer<typeof createCountryGroupSchema>;

  const {
    register,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateCountryGroupFormValues>({
    resolver: zodResolver(createCountryGroupSchema),
    defaultValues: {
      title: '',
      description: '',
      countries: [],
      carriers: [],
    },
  });
  const carrierMenuRef = React.useRef<HTMLDivElement>(null);
  const countriesMenuRef = React.useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLFormElement>(null);
  const editFormRef = useRef<HTMLFormElement>(null);
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [carrierSearch, setCarrierSearch] = useState<string>('');
  const [carrierMenuVisible, setCarrierMenuVisible] = useState<boolean>(false);
  const [countriesMenuVisible, setCountriesMenuVisible] = useState<boolean>(false);
  const [selectedCountries, setSelectedCountries] = useState<any[]>([]);
  const [selectedCarriers, setSelectedCarriers] = useState<any[]>([]);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('carrier-analytics');

  const handleSelectCountry = (country: any) => {
    setSelectedCountries(prev => {
      if (prev.some(item => item.id === country.id)) return prev;
      return [...prev, country];
    });
  };

  const handleSelectCarrier = (carrier: any) => {
    setSelectedCarriers(prev => {
      if (prev.some(item => item.id === carrier.id)) return prev;
      return [...prev, carrier];
    });
  };

  const handleSelectAllCarriers = (items: any[]) => {
    setSelectedCarriers(prev => {
      const existing = new Set(prev.map(c => c.id));
      return [...prev, ...items.filter(c => !existing.has(c.id))];
    });
    setCarrierMenuVisible(false);
  };

  // const { data: countryGroup } = useQuery({
  //   queryKey: ['country_group', numericId],
  //   queryFn: () => getOneCountryGroup(numericId),

  //   enabled: editMode,
  // });

  const { data: countryGroup } = useOneCountryGroupQuery(numericId, { enabled: editMode });

  const { data: countries } = useAllCountriesForSelectionQuery();
  const { data: carriers } = useCarriersQuery();

  const availableCountries = useMemo(
    () => (countries || []).filter(c => !selectedCountries.some(s => s.id === c.id)),
    [countries, selectedCountries],
  );
  const availableCarriers = useMemo(
    () => (carriers || []).filter(c => !selectedCarriers.some(s => s.id === c.id)),
    [carriers, selectedCarriers],
  );

  const createMutation = useCountryGroupCreate();
  const updateMutation = useCountryGroupUpdateData();

  useEffect(() => {
    setValue(
      'countries',
      selectedCountries.map(({ id }) => ({ id })),
    );
    setValue(
      'carriers',
      selectedCarriers.map(({ id }) => ({ id })),
    );
  }, [selectedCountries, selectedCarriers, setValue]);

  useEffect(() => {
    if (editMode && countryGroup) {
      const countries = countryGroup.countries || [];
      const carriers = countryGroup.carriers || [];

      setSelectedCountries(countries);
      setSelectedCarriers(carriers);

      setValue(
        'countries',
        countries.map(({ id }) => ({ id })),
      );
      setValue(
        'carriers',
        carriers.map(({ id }) => ({ id })),
      );
    }
  }, [editMode, countryGroup, setValue]);

  useEffect(() => {}, [countrySearch, carrierSearch]);

  const resolveMutationError = (e: any, fallbackKey: string) => {
    const detail = e?.detail;
    return typeof detail === 'string' && detail.trim() ? detail : t(fallbackKey);
  };

  const createCountryGroup = async (data: any) => {
    try {
      await createMutation.mutateAsync({
        body: data,
      });
      toast.success(t('success.country_group_created'));
      if (onCustomClose) {
        onCustomClose();
      }
      onClose();
    } catch (e) {
      toast.error(resolveMutationError(e, 'errors.country_group_creation_failed'), { autoClose: 5000 });
    }
  };

  const updateCountryGroup = async (id: number, data: any) => {
    try {
      await updateMutation.mutateAsync({
        params: {
          path: { country_group_id: id },
        },
        body: data,
      });
      toast.success(t('success.country_group_updated'));
      if (onCustomClose) {
        onCustomClose();
      }
      onClose();
    } catch (e) {
      toast.error(resolveMutationError(e, 'errors.country_group_update_failed'), { autoClose: 5000 });
    }
  };

  const onSubmit = async (formData: CreateCountryGroupFormValues) => {
    const payload = {
      countries: selectedCountries.map(c => ({ id: c.id })),
      carriers: selectedCarriers.map(c => ({ id: c.id })),
      name: formData.title,
      description: formData.description,
    };
    if (editMode) {
      await updateCountryGroup(numericId, payload);
    } else {
      await createCountryGroup(payload);
    }
  };

  const triggerSubmit = () => {
    if (editMode) {
      editFormRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      addFormRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const filteredCountries = useMemo(
    () => selectedCountries.filter(country => country.name.toLowerCase().includes(countrySearch.toLowerCase())),
    [selectedCountries, countrySearch],
  );

  const filteredCarriers = useMemo(
    () => selectedCarriers.filter(carrier => carrier.name.toLowerCase().includes(carrierSearch.toLowerCase())),
    [selectedCarriers, carrierSearch],
  );

  useOutsideClick(mainRef, () => {
    if (onCustomClose) {
      onCustomClose();
    }
    onClose();
  });

  useEventListeners([{ event: 'keydown' }], e => {
    if ((e as KeyboardEvent).key === 'Escape') {
      if (onCustomClose) {
        onCustomClose();
      }
      onClose();
    }
  });

  useEffect(() => {
    if (countryGroup) {
      setValue('title', countryGroup.name || '');
      setValue('description', countryGroup.description || '');
    }
  }, [countryGroup, setValue]);

  return (
    <div
      ref={mainRef}
      data-overlay-handles-outside="true"
      data-overlay-handles-escape="true"
      data-overlay-type="country-group"
      style={{ height: '100%', overflow: 'auto' }}>
      <ContainerBody className="h-full">
        {!editMode ? (
          <form onSubmit={handleSubmit(onSubmit)} ref={addFormRef} className="flex flex-col w-full items-start">
            <div className="flex flex-col items-start gap-[12px] max-w-[455px] w-full">
              <ControlledInput
                {...register('title')}
                control={control}
                label={t('general.title_group')}
                placeholder=""
                className="w-full"
              />
              <ControlledInput
                control={control}
                {...register('description')}
                label={t('general.description')}
                placeholder=""
                className="w-full"
              />
            </div>
            <Divider className="mt-[25px] mb-[40px] w-full" />
            <div className="flex flex-col gap-[20px] w-full">
              <span className="typo-title-24">{t('general.countries')}</span>
              {errors.countries && (
                <div className="flex align-center gap-x-4 mt-8 status-error-full">
                  <Icon name="error" width={16} height={16} />
                  <p className="typo-text-12--bold">{errors.countries.message}</p>
                </div>
              )}
              {accessRights?.read_write && (
                <div className="flex flex-col gap-[16px] md:flex-row w-full items-start md:items-center justify-between">
                  <Input
                    value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    placeholder={t('navbar.search')}
                    className="max-w-[455px] w-full"
                    icon="search"
                  />
                  <Button
                    icon="plus"
                    title={t('general.countries')}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    onClick={() => setCountriesMenuVisible(!countriesMenuVisible)}
                  />
                </div>
              )}
              <div className="relative flex flex-col gap-[20px] w-full">
                {filteredCountries.length ? (
                  <div className="flex justify-center py-[20px] px-[20px] md:px-[40px] max-h-[380px] h-auto md:h-[138px] w-full rounded-[8px] surface-elevated border border-subtle">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-auto overflow-y-auto w-full">
                      {filteredCountries.map((country: any) => (
                        <div
                          key={country.id}
                          role="none"
                          onClick={
                            accessRights?.read_write
                              ? () => setSelectedCountries(prev => prev.filter(c => c !== country))
                              : () => {}
                          }
                          className={`flex items-center gap-[8px] px-[8px] py-[8px] rounded-[4px] max-h-[32px] transition-all cursor-pointer hover:surface-elevated-more group typo-text-12--bold ${
                            accessRights?.read_write ? 'hover:status-error-full' : ''
                          }`}>
                          {accessRights?.read_write && (
                            <Icon
                              name="delete"
                              width={14}
                              height={14}
                              className="status-error-full hidden group-hover:block"
                            />
                          )}
                          <span>{country.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    onClick={accessRights?.read_write ? () => setCountriesMenuVisible(!countriesMenuVisible) : () => {}}
                    className="flex justify-center items-center cursor-pointer h-[180px] w-full rounded-[8px] surface-elevated border-subtle">
                    <img src="/no-data.svg" alt="no data" className="w-[100px] h-[100px]" />
                  </div>
                )}
                {countriesMenuVisible && (
                  <div className="absolute -top-10 right-5 z-20" ref={countriesMenuRef}>
                    <ListItemsMenu
                      type="country"
                      items={availableCountries}
                      title={t('general.add_countries')}
                      onClose={() => setCountriesMenuVisible(false)}
                      onSelect={handleSelectCountry}
                      searchString={countrySearch}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-[20px] w-full mt-[40px]">
              <span className="typo-title-24">{t('general.carrier')}</span>
              {errors.carriers && (
                <div className="flex align-center gap-x-4 mt-8 status-error-full">
                  <Icon name="error" width={16} height={16} />
                  <p className="typo-text-12--bold">{errors.carriers.message}</p>
                </div>
              )}
              <div className="flex flex-col gap-[16px] md:flex-row w-full items-start md:items-center justify-between">
                <Input
                  value={carrierSearch}
                  onChange={e => setCarrierSearch(e.target.value)}
                  placeholder={t('navbar.search')}
                  className="max-w-[455px] w-full"
                  icon="search"
                />
                <Button
                  icon="plus"
                  title={t('general.carrier')}
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  onClick={() => setCarrierMenuVisible(true)}
                />
              </div>
              <div className="relative flex flex-col gap-[20px] w-full">
                {filteredCarriers.length ? (
                  <div className="flex justify-center py-[20px] px-[20px] md:px-[40px] max-h-[380px] h-auto md:h-[138px] w-full rounded-[8px] surface-elevated border border-subtle">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-auto overflow-y-auto w-full">
                      {filteredCarriers.map((carrier: any) => (
                        <div
                          key={carrier.id}
                          role="none"
                          onClick={
                            accessRights?.read_write
                              ? () => setSelectedCarriers(prev => prev.filter(c => c !== carrier))
                              : () => {}
                          }
                          className={`flex items-center gap-[8px] px-[8px] py-[8px] rounded-[4px] max-h-[32px] transition-all cursor-pointer hover:surface-elevated-more group typo-text-12--bold ${
                            accessRights?.read_write ? 'hover:status-error-full' : ''
                          }`}>
                          {accessRights?.read_write && (
                            <Icon
                              name="delete"
                              width={14}
                              height={14}
                              className="status-error-full hidden group-hover:block"
                            />
                          )}
                          <Image
                            imageUrl={carrier?.logo_url || ''}
                            alt="logo"
                            className="w-[16px] h-[16px] rounded-[4px]"
                            fallbackComponent={<Icon name="package" width={20} height={20} />}
                          />
                          <span>{carrier.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    onClick={accessRights?.read_write ? () => setCarrierMenuVisible(!carrierMenuVisible) : () => {}}
                    className="flex justify-center items-center cursor-pointer h-[180px] w-full rounded-[8px] surface-elevated border-subtle">
                    <img src="/no-data.svg" alt="no data" className="w-[100px] h-[100px]" />
                  </div>
                )}
                {carrierMenuVisible && (
                  <div className="absolute -top-10 right-5 z-20" ref={carrierMenuRef}>
                    <ListItemsMenu
                      type="carrier"
                      items={availableCarriers}
                      title={t('general.add_carrier')}
                      onClose={() => setCarrierMenuVisible(false)}
                      onSelect={handleSelectCarrier}
                      onSelectAll={handleSelectAllCarriers}
                      searchString={carrierSearch}
                    />
                  </div>
                )}
              </div>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} ref={editFormRef} className="flex flex-col w-full items-start">
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-center w-full gap-2">
                <div className="w-1/3 typo-text-14--bold text-faded">{t('general.title_group')}</div>
                <ControlledInput
                  name="title"
                  control={control}
                  placeholder={t('general.title_group')}
                  className="w-2/3"
                />
              </div>
              <div className="flex items-center w-full gap-2">
                <div className="w-1/3 typo-text-14--bold text-faded">{t('general.description')}</div>
                <ControlledInput
                  name="description"
                  control={control}
                  placeholder={t('general.description')}
                  className="w-2/3"
                />
              </div>
            </div>
            <Divider className="mt-[25px] mb-[40px] w-full" />
            <div className="flex flex-col gap-[20px] w-full">
              <span className="typo-title-24">{t('general.countries')}</span>
              {errors.countries && (
                <div className="flex align-center gap-x-4 mt-8 status-error-full">
                  <Icon name="error" width={16} height={16} />
                  <p className="typo-text-12--bold">{errors.countries.message}</p>
                </div>
              )}
              {accessRights?.read_write && (
                <div className="flex flex-col gap-[16px] md:flex-row w-full items-start md:items-center justify-between">
                  <Input
                    value={countrySearch}
                    onChange={e => setCountrySearch(e.target.value)}
                    placeholder={t('navbar.search')}
                    className="max-w-[455px] w-full"
                    icon="search"
                  />
                  <Button
                    icon="plus"
                    title={t('general.countries')}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    onClick={() => setCountriesMenuVisible(!countriesMenuVisible)}
                  />
                </div>
              )}

              <div className="relative flex flex-col gap-[20px] w-full">
                {filteredCountries?.length ? (
                  <div className="flex justify-center py-[20px] px-[20px] md:px-[40px] max-h-[380px] h-auto md:h-[138px] w-full rounded-[8px] surface-elevated border border-subtle">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-auto overflow-y-auto w-full">
                      {filteredCountries.map((country: any) => (
                        <div
                          key={country.id}
                          role="none"
                          onClick={
                            accessRights?.read_write
                              ? () => setSelectedCountries(prev => prev.filter(c => c !== country))
                              : () => {}
                          }
                          className={`flex items-center gap-[8px] px-[8px] py-[8px] rounded-[4px] max-h-[32px] transition-all cursor-pointer hover:surface-elevated-more group typo-text-12--bold ${
                            accessRights?.read_write ? 'hover:status-error-full' : ''
                          }`}>
                          {accessRights?.read_write && (
                            <Icon
                              name="delete"
                              width={14}
                              height={14}
                              className="status-error-full hidden group-hover:block"
                            />
                          )}
                          <span>{country.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    onClick={accessRights?.read_write ? () => setCountriesMenuVisible(!countriesMenuVisible) : () => {}}
                    className="flex justify-center items-center cursor-pointer h-[180px] w-full rounded-[8px] surface-elevated border-subtle">
                    <img src="/no-data.svg" alt="no data" className="w-[100px] h-[100px]" />
                  </div>
                )}

                {countriesMenuVisible && (
                  <div className="absolute -top-10 right-5 z-20" ref={countriesMenuRef}>
                    <ListItemsMenu
                      type="country"
                      items={availableCountries}
                      title={t('general.add_countries')}
                      onClose={() => setCountriesMenuVisible(false)}
                      onSelect={handleSelectCountry}
                      searchString={countrySearch}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-[20px] w-full mt-[40px]">
              <span className="typo-title-24">{t('general.carrier')}</span>
              {errors.carriers && (
                <div className="flex align-center gap-x-4 mt-8 status-error-full">
                  <Icon name="error" width={16} height={16} />
                  <p className="typo-text-12--bold">{errors.carriers.message}</p>
                </div>
              )}
              {accessRights?.read_write && (
                <div className="flex flex-col gap-[16px] md:flex-row w-full items-start md:items-center justify-between">
                  <Input
                    value={carrierSearch}
                    onChange={e => setCarrierSearch(e.target.value)}
                    placeholder={t('navbar.search')}
                    className="max-w-[455px] w-full"
                    icon="search"
                  />
                  <Button
                    icon="plus"
                    title={t('general.carrier')}
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    onClick={() => setCarrierMenuVisible(true)}
                  />
                </div>
              )}

              <div className="relative flex flex-col gap-[20px] w-full">
                {filteredCarriers.length ? (
                  <div className="flex justify-center py-[20px] px-[20px] md:px-[40px] max-h-[380px] h-auto md:h-[138px] w-full rounded-[8px] surface-elevated border border-subtle">
                    <div className="grid grid-cols-2 gap-x-auto overflow-y-auto w-full h-full">
                      {filteredCarriers.map((carrier: any) => (
                        <div
                          key={carrier.id}
                          role="none"
                          onClick={
                            accessRights?.read_write
                              ? () => setSelectedCarriers(prev => prev.filter(c => c !== carrier))
                              : () => {}
                          }
                          className={`flex items-center gap-[8px] px-[8px] py-[8px] rounded-[4px] max-h-[32px] transition-all cursor-pointer hover:surface-elevated-more group typo-text-12--bold ${
                            accessRights?.read_write ? 'hover:status-error-full' : ''
                          }`}>
                          {accessRights?.read_write && (
                            <Icon
                              name="delete"
                              width={14}
                              height={14}
                              className="status-error-full hidden group-hover:block"
                            />
                          )}
                          <Image
                            imageUrl={carrier?.logo_url || ''}
                            alt="logo"
                            className="w-[16px] h-[16px] rounded-[4px]"
                            fallbackComponent={<Icon name="package" width={20} height={20} />}
                          />
                          <span>{carrier.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    onClick={accessRights?.read_write ? () => setCarrierMenuVisible(!carrierMenuVisible) : () => {}}
                    className="flex justify-center items-center cursor-pointer h-[180px] w-full rounded-[8px] surface-elevated border-subtle">
                    <img src="/no-data.svg" alt="no data" className="w-[100px] h-[100px]" />
                  </div>
                )}
                {carrierMenuVisible && (
                  <div className="absolute -top-10 right-5 z-20" ref={carrierMenuRef}>
                    <ListItemsMenu
                      type="carrier"
                      items={availableCarriers}
                      title={t('general.add_carrier')}
                      onClose={() => setCarrierMenuVisible(false)}
                      onSelect={handleSelectCarrier}
                      onSelectAll={handleSelectAllCarriers}
                      searchString={carrierSearch}
                    />
                  </div>
                )}
              </div>
            </div>
          </form>
        )}

        <div className="flex flex-col">
          <Divider className="mt-[24px] md:mt-[40px]" />
          <div className="flex w-full gap-[12px] py-[16px] justify-end items-center">
            <Button
              title={t('general.cancel')}
              onClick={() => (onCustomClose ? onCustomClose() : onClose())}
              tone={ButtonToneProps.SECONDARY}
            />
            <Button title={t('general.save')} tone={ButtonToneProps.PRIMARY_BLUE} onClick={triggerSubmit} />
          </div>
        </div>
      </ContainerBody>
    </div>
  );
}
